import type { QueryClient } from '@tanstack/react-query'
import { notFound, redirect } from '@tanstack/react-router'

import { isSessionGone } from '@/shared/api/errors'
import { clinicModulesQueryOptions } from '@/shared/control-center/modules'
import {
  canAccessControlCenterSection, canAccessModuleAdminView, controlCenterSectionViews, defaultControlCenterSection,
  defaultControlCenterView, isControlCenterSection, isModuleAdminView,
  CLINIC_MANAGEMENT_GROUPS, canAccessPlatformSection, defaultPlatformSection, type ClinicManagementSection,
} from '@/shared/control-center/navigation'
import { LOGIN_ROUTE, safeReturnTarget } from '@/shared/routing/returnTarget'
import { externalAuthorizationDestination, externalAuthorizationFromHref } from '@/shared/routing/externalAuthorization'
import { defaultScheduleSection, isScheduleSection } from '@/shared/schedule/navigation'
import { CAPABILITIES, hasAnyCapability, hasCapability, type CapabilityName } from '@/shared/session/capabilities'
import { isProfileSection, profileSectionOffered } from '@/shared/session/profileNavigation'
import { sessionQueryOptions } from '@/shared/session/session'
import { hasOrganization, type AccountSession, type Session } from '@/shared/session/types'
import { canOpenTrackerReports } from '@/shared/tracker/reports/trackerReports'
import { WORKSPACE_DESTINATIONS, workspaceLanding } from '@/shared/workspace/workspaceDestinations'

export interface RouterContext { queryClient: QueryClient }
type ContextInput = { context: RouterContext }
type LocationInput = ContextInput & { location: { href: string } }
type SectionInput = ContextInput & { params: { section: string } }

function assertOrganization(session: AccountSession): asserts session is Session {
  if (!hasOrganization(session)) throw redirect({ to: '/organizations', replace: true })
}

function assertSessionCapability(session: AccountSession, capability: CapabilityName): void {
  assertOrganization(session)
  if (!hasCapability(session, capability)) throw notFound()
}

// Navigation refusal is not-found; Core independently authorizes every request.
export function requireCapability(capability: CapabilityName) {
  return async ({ context }: ContextInput): Promise<void> => {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
    assertSessionCapability(session, capability)
  }
}

export function requireAnyCapability(capabilities: readonly CapabilityName[]) {
  return async ({ context }: ContextInput): Promise<void> => {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
    assertOrganization(session)
    if (!hasAnyCapability(session, capabilities)) throw notFound()
  }
}

export async function requirePasswordChange({ context, search }: ContextInput & { search: { redirect?: string } }): Promise<void> {
  try {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
    if (!session.identity.passwordChangeRequired) {
      const intent = externalAuthorizationFromHref(search.redirect ?? '')
      throw redirect(intent.authorize ? externalAuthorizationDestination(intent.authorize) : { to: safeReturnTarget(search.redirect), replace: true })
    }
  } catch (error) {
    if (isSessionGone(error)) throw redirect({ to: LOGIN_ROUTE, search: externalAuthorizationFromHref(search.redirect ?? ''), replace: true })
    throw error
  }
}

export async function requireAccountConnectors({ context, location }: LocationInput): Promise<void> {
  try {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
    if (session.identity.passwordChangeRequired) throw redirect({ to: '/password-change', search: { redirect: location.href }, replace: true })
    if (session.identity.accountKind !== 'developer') throw notFound()
  } catch (error) {
    if (isSessionGone(error)) throw redirect({ to: LOGIN_ROUTE, replace: true })
    throw error
  }
}

export async function requireOrganizationSelection({ context, location }: LocationInput): Promise<void> {
  try {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
    if (session.identity.passwordChangeRequired) throw redirect({ to: '/password-change', search: { redirect: location.href }, replace: true })
  } catch (error) {
    if (isSessionGone(error)) throw redirect({ to: LOGIN_ROUTE, search: externalAuthorizationFromHref(location.href), replace: true })
    throw error
  }
}

export async function requireApplication({ context, location }: LocationInput): Promise<void> {
  try {
    const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
    const consent = externalAuthorizationFromHref(location.href)
    if (consent.authorize && (!hasOrganization(session) || !hasCapability(session, CAPABILITIES.appAccess))) {
      throw redirect({ to: '/organizations', search: consent, replace: true })
    }
    assertSessionCapability(session, CAPABILITIES.appAccess)
    if (session.identity.passwordChangeRequired) {
      throw redirect({ to: '/password-change', search: { redirect: location.href }, replace: true })
    }
  } catch (error) {
    if (isSessionGone(error)) throw redirect({ to: LOGIN_ROUTE, search: externalAuthorizationFromHref(location.href) })
    // Transport and malformed-context errors must not resemble a signed-in shell.
    throw error
  }
}

export async function enterFinance({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  const landing = workspaceLanding(WORKSPACE_DESTINATIONS.finance, session)
  if (landing === null) throw notFound()
  throw redirect({ to: landing.to, replace: true })
}

/** The processing area admits whom its workspace tile admits, by the same predicate. */
export async function requireProcessing({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!WORKSPACE_DESTINATIONS.processing.admits(session)) throw notFound()
}

export async function enterSchedule({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  const section = defaultScheduleSection(session)
  if (section === null) throw notFound()
  if (section === 'calendar') return
  throw redirect({ to: '/schedule/$section', params: { section }, replace: true })
}

export async function requireScheduleSection({ context, params }: SectionInput): Promise<void> {
  if (!isScheduleSection(params.section)) throw notFound()
  const capability = params.section === 'working-calendar' ? CAPABILITIES.workingCalendarsRead
    : params.section === 'availability' ? CAPABILITIES.availabilityRead
      : params.section === 'resources' ? CAPABILITIES.resourcesRead : CAPABILITIES.visitTypesRead
  await requireCapability(capability)({ context })
}

export async function enterControlCenter({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  const section = defaultControlCenterSection(session)
  if (!section) throw notFound()
  throw redirect({ to: '/control-center/$section', params: { section }, replace: true })
}

function isClinicSection(section: string): section is ClinicManagementSection {
  return isControlCenterSection(section) && CLINIC_MANAGEMENT_GROUPS.some(group => group.sections.some(item => item === section))
}

// A profile section the session is not offered is not found, like any refused
// section; Core still authorizes each of its requests.
export async function requireProfileSection({ context, params }: SectionInput): Promise<void> {
  if (!isProfileSection(params.section)) throw notFound()
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  if (!profileSectionOffered(session, params.section)) throw notFound()
}

export function requireDesignDrafts({ context }: ContextInput): Promise<void> {
  return requireProfileSection({ context, params: { section: 'design-drafts' } })
}

// The account-only drafts admit the same accounts without a selected clinic.
export async function requireAccountDesignDrafts(input: LocationInput): Promise<void> {
  await requireOrganizationSelection(input)
  const session = await input.context.queryClient.ensureQueryData(sessionQueryOptions())
  if (!session.identity.designAccess) throw notFound()
}

export async function requireControlCenterSection({ context, params }: SectionInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!isClinicSection(params.section) || !canAccessControlCenterSection(session, params.section)) throw notFound()
  // Split sections use one canonical address per screen.
  const view = defaultControlCenterView(params.section)
  if (view) throw redirect({ to: '/control-center/$section/$view', params: { section: params.section, view }, replace: true })
}

export async function requireControlCenterView({ context, params }: ContextInput & { params: { section: string; view: string } }): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!isClinicSection(params.section) || !canAccessControlCenterSection(session, params.section)) throw notFound()
  if (params.section === 'modules') {
    const items = await context.queryClient.ensureQueryData(clinicModulesQueryOptions())
    if (!items.some(item => item.moduleCode === params.view)) throw notFound()
    throw redirect({ to: '/control-center/modules/$moduleCode/$moduleView', params: { moduleCode: params.view, moduleView: 'title' }, replace: true })
  }
  if (!controlCenterSectionViews(params.section)?.includes(params.view)) throw notFound()
}

/** One employee's card: the Users section's own reader, nothing more. */
export async function requireControlCenterUserCard({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!canAccessControlCenterSection(session, 'users')) throw notFound()
}

export async function requireControlCenterModule({ context, params }: ContextInput & { params: { moduleCode: string; moduleView: string } }): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!isModuleAdminView(params.moduleView) || !canAccessModuleAdminView(session, params.moduleView)) throw notFound()
  const items = await context.queryClient.ensureQueryData(clinicModulesQueryOptions())
  if (!items.some(item => item.moduleCode === params.moduleCode)) throw notFound()
}

export async function requireTrackerReports({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!canOpenTrackerReports(session)) throw notFound()
}

export async function enterPlatform({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  const section = defaultPlatformSection(session)
  if (!section) throw notFound()
  throw redirect({ to: '/platform/$section', params: { section }, replace: true })
}

export async function requirePlatformSection({ context, params }: SectionInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!canAccessPlatformSection(session, params.section)) throw notFound()
  if (params.section === 'ai') throw redirect({ to: '/platform/$section/$view', params: { section: 'ai', view: 'credentials' }, replace: true })
}

export async function requirePlatformView({ context, params }: ContextInput & { params: { section: string; view: string } }): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (params.section !== 'ai' || !canAccessPlatformSection(session, 'ai') || !['files', 'credentials', 'tools'].includes(params.view)) throw notFound()
}

export async function requireImagingImports({ context }: ContextInput): Promise<void> {
  const session = await context.queryClient.ensureQueryData(sessionQueryOptions())
  assertOrganization(session)
  if (!canAccessControlCenterSection(session, 'uploads')) throw notFound()
}
